'use client';

import React, { useTransition } from 'react';
import { Formik } from 'formik';
import { TextInput, HoneypotField } from '@/components/formComponents';
import { toast } from 'react-hot-toast';
import { Button, PageMainCard } from '@/components';
import validationSchema from '@/validation/contactFormValidation';
import { FormRow } from '@/components/formComponents/helpers';
import Link from 'next/link';
import OpeningTimes from './OpeningTimes';
import { submitForm } from '@/actions/contact-form';
import { OpeningTimesProps } from './OpeningTimes';

interface ContactFormValues {
    fullName: string;
    email: string;
    telephone: string;
    message: string;
}

const ContactForm = ({
    openingTimesData,
    companyDetailsData,
}: OpeningTimesProps) => {
    const [isPending, startTransition] = useTransition();
    const initialValues: ContactFormValues = {
        fullName: '',
        email: '',
        telephone: '',
        message: '',
    };

    return (
        <PageMainCard>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnBlur={false}
                onSubmit={async (
                    values,
                    { resetForm, setFieldError, setSubmitting }
                ) => {
                    startTransition(async () => {
                        try {
                            // Create FormData from values
                            const formData = new FormData();
                            formData.append('fullName', values.fullName);
                            formData.append('email', values.email);
                            formData.append('telephone', values.telephone);
                            formData.append('message', values.message);

                            // Call server action
                            const result = await submitForm(formData);

                            if (result.isBot) {
                                // Silently swallow — bot thinks it succeeded, we do nothing
                                return;
                            }

                            if (result.success) {
                                toast.success(
                                    "Contact form submitted successfully, thanks! We'll get back to you as soon as possible."
                                );
                                resetForm();
                            } else {
                                // Handle validation errors from server
                                if (result.fieldErrors) {
                                    // Set individual field errors
                                    Object.entries(result.fieldErrors).forEach(
                                        ([field, message]) => {
                                            setFieldError(
                                                field,
                                                message as string
                                            );
                                        }
                                    );
                                }

                                // Show general error toast
                                toast.error(
                                    result.error ||
                                        'Something went wrong. Please try again.'
                                );

                                // Log detailed errors for debugging
                                if (result.validationErrors) {
                                    console.log(
                                        'Validation errors:',
                                        result.validationErrors
                                    );
                                }
                            }
                        } catch (error) {
                            console.error('Form submission error:', error);
                            toast.error(
                                'An unexpected error occurred. Please try again.'
                            );
                        } finally {
                            setSubmitting(false);
                        }
                    });
                }}
            >
                {({ handleSubmit, isSubmitting }) => {
                    return (
                        <>
                            <form onSubmit={handleSubmit} className="w-full">
                                <div className="mb-8">
                                    If you would rather send us an email, please
                                    address it to:{' '}
                                    <Link
                                        href="mailto:bookings@pennycarhire.co.uk"
                                        className="underline"
                                    >
                                        bookings@pennycarhire.co.uk
                                    </Link>
                                </div>
                                <FormRow
                                    label={`Your full name:`}
                                    content={
                                        <TextInput
                                            name="fullName"
                                            placeholder="Full Name:"
                                        />
                                    }
                                />
                                <FormRow
                                    label={`Email address:`}
                                    content={
                                        <TextInput
                                            name="email"
                                            placeholder="Email:"
                                            type="email"
                                        />
                                    }
                                />
                                <FormRow
                                    label={`Telephone number:`}
                                    content={
                                        <TextInput
                                            name="telephone"
                                            placeholder="Telephone:"
                                            type="tel"
                                        />
                                    }
                                />
                                <FormRow
                                    label={`Your message:`}
                                    content={
                                        <TextInput
                                            name="message"
                                            placeholder="Message:"
                                            container="textarea"
                                        />
                                    }
                                />
                                <HoneypotField />
                                <div className="flex justify-between">
                                    <div className="flex text-sm italic items-center required text-gray-500">
                                        Required
                                    </div>
                                    <div className="w-full justify-end flex">
                                        <Button
                                            label={
                                                isPending || isSubmitting
                                                    ? 'Submitting...'
                                                    : 'Submit Message'
                                            }
                                            disabled={isPending || isSubmitting}
                                        />
                                    </div>
                                </div>
                            </form>
                            <OpeningTimes
                                openingTimesData={openingTimesData}
                                companyDetailsData={companyDetailsData}
                            />
                        </>
                    );
                }}
            </Formik>
        </PageMainCard>
    );
};

export default ContactForm;
